import { useCallback, useMemo } from 'react';
import {
  useAppDataSourceRecordStore,
  useDataSourceRecordStore,
  useDataSourceRecordStoreManager,
} from './DataSourceRecordStoreProvider';
import type { UnsavedDataSource } from './types';

export const useGetDataSourceRecordStoreStateByInstanceId = () => {
  const manager = useDataSourceRecordStoreManager();
  return useCallback((instanceId?: string) => manager.getStore(instanceId).getState(), [manager]);
};

export const useGetDataSourcesByInstanceId = () => {
  const getDataSourceRecordStoreState = useGetDataSourceRecordStoreStateByInstanceId();
  const appDataSourceRecordStore = useAppDataSourceRecordStore();
  return useCallback(
    (instanceId?: string) => {
      const instanceDataSources = getDataSourceRecordStoreState(instanceId).dataSources;
      const appDataSources = appDataSourceRecordStore.getState().dataSources;
      return { ...appDataSources, ...instanceDataSources };
    },
    [getDataSourceRecordStoreState, appDataSourceRecordStore],
  );
};

export const usePageDataSourceRecords = () => {
  return useDataSourceRecordStore().hooks.useDataSources();
};

export const useAppDataSourceRecords = () => {
  return useAppDataSourceRecordStore().hooks.useDataSources();
};

export function useDataSourceRecords() {
  const pageDataSourceRecords = usePageDataSourceRecords();
  const appDataSourceRecords = useAppDataSourceRecords();

  const dataSources = useMemo(() => {
    return { ...appDataSourceRecords, ...pageDataSourceRecords };
  }, [appDataSourceRecords, pageDataSourceRecords]);

  return dataSources;
}

export const usePageUnsavedDataSourceRecords = () => {
  return useDataSourceRecordStore().hooks.useUnsavedDataSources();
};

export const useAppUnsavedDataSourceRecords = () => {
  return useAppDataSourceRecordStore().hooks.useUnsavedDataSources();
};

export const useUnsavedDataSourceRecords = () => {
  const pageUnsavedDataSourceRecords = usePageUnsavedDataSourceRecords();
  const appUnsavedDataSourceRecords = useAppUnsavedDataSourceRecords();

  const unsavedDataSources = useMemo(() => {
    return { ...appUnsavedDataSourceRecords, ...pageUnsavedDataSourceRecords };
  }, [appUnsavedDataSourceRecords, pageUnsavedDataSourceRecords]);

  return unsavedDataSources;
};

export const usePageDataSourceSchemas = () => {
  return useDataSourceRecordStore().hooks.useDataSourceSchemas();
};

export const useAppDataSourceSchemas = () => {
  return useAppDataSourceRecordStore().hooks.useDataSourceSchemas();
};

export const usePageDataSourceSchemaErrors = () => {
  return useDataSourceRecordStore().hooks.useDataSourceSchemaErrors();
};

export const useAppDataSourceSchemaErrors = () => {
  return useAppDataSourceRecordStore().hooks.useDataSourceSchemaErrors();
};

export const useDataSourceSchemaErrors = () => {
  const pageErrors = usePageDataSourceSchemaErrors();
  const appErrors = useAppDataSourceSchemaErrors();

  const errors = useMemo(() => {
    return { ...appErrors, ...pageErrors };
  }, [appErrors, pageErrors]);

  return errors;
};

export const useDataSourceRecordsSchemas = () => {
  const pageDataSourceSchemas = usePageDataSourceSchemas();
  const appDataSourceSchemas = useAppDataSourceSchemas();

  const dataSourceSchemas = useMemo(() => {
    return { ...appDataSourceSchemas, ...pageDataSourceSchemas };
  }, [appDataSourceSchemas, pageDataSourceSchemas]);

  return dataSourceSchemas;
};

/**
 * Merges the data sources from the store with the unsaved data sources.
 * This is done because in the data source listing panel, we show the saved and unsaved data sources,
 * and we want to override the saved data sources with the unsaved data sources when they exist.
 */
export function useMergedPageDataSources() {
  const pageDataSourceRecords = usePageDataSourceRecords();
  const pageUnsavedDataSourceRecords = usePageUnsavedDataSourceRecords();

  const adaptedDataSources = useMemo(() => {
    const allDataSourcesKeys = Object.keys(pageDataSourceRecords);
    return allDataSourcesKeys.reduce<Record<string, UnsavedDataSource | undefined>>((acc, id) => {
      acc[id] = pageUnsavedDataSourceRecords[id] || pageDataSourceRecords[id];
      return acc;
    }, {});
  }, [pageDataSourceRecords, pageUnsavedDataSourceRecords]);

  return { dataSources: adaptedDataSources };
}

/**
 * Merges the data sources from the store with the unsaved data sources.
 * Returns the saved and unsaved app level data sources.
 */
export function useMergedAppDataSources() {
  const appDataSourceRecords = useAppDataSourceRecords();
  const appUnsavedDataSourceRecords = useAppUnsavedDataSourceRecords();

  const adaptedDataSources = useMemo(() => {
    const allDataSourcesKeys = Object.keys(appDataSourceRecords);
    return allDataSourcesKeys.reduce<Record<string, UnsavedDataSource | undefined>>((acc, id) => {
      acc[id] = appUnsavedDataSourceRecords[id] || appDataSourceRecords[id];
      return acc;
    }, {});
  }, [appDataSourceRecords, appUnsavedDataSourceRecords]);

  return { dataSources: adaptedDataSources };
}

/**
 * Merges the data sources from the store with the unsaved data sources.
 * Returns the saved and unsaved app and page level data sources.
 */
export function useMergedDataSources() {
  const { dataSources: appDataSources } = useMergedAppDataSources();
  const { dataSources: pageDataSources } = useMergedPageDataSources();

  const mergedDataSources = useMemo(() => {
    return { ...appDataSources, ...pageDataSources };
  }, [appDataSources, pageDataSources]);

  return { dataSources: mergedDataSources };
}

export function useDataSourceRecord(dataSourceId: string) {
  const pageDataSourceRecord = useDataSourceRecordStore().hooks.useDataSources()[dataSourceId];
  const appDataSourceRecord = useAppDataSourceRecordStore().hooks.useDataSources()[dataSourceId];

  return pageDataSourceRecord || appDataSourceRecord;
}

export function useUnsavedDataSourceRecord(dataSourceId: string) {
  const pageUnsavedDataSourceRecord =
    useDataSourceRecordStore().hooks.useUnsavedDataSources()[dataSourceId];
  const appUnsavedDataSourceRecord =
    useAppDataSourceRecordStore().hooks.useUnsavedDataSources()[dataSourceId];

  return pageUnsavedDataSourceRecord || appUnsavedDataSourceRecord;
}
